import { RESEARCH_AGENT_BASE_URL } from "@/src/common/config"
import {
  RESEARCH_AGENT_STREAM_ENDPOINT,
  RESEARCH_AGENT_THREADS_ENDPOINT
} from "@/src/common/endpoints"

export type ThreadCreationRequest = {
  title: string
}

export type ThreadResponse = {
  id: string
  title: string
  user_id: number
  created: string
  updated: string
}

export class ResearchAgentAPI {
  private getBaseUrl(): string {
    return RESEARCH_AGENT_BASE_URL.replace(/\/$/, "")
  }

  public async createThread(
    payload: ThreadCreationRequest,
    token: string
  ): Promise<ThreadResponse> {
    const response = await fetch(
      `${this.getBaseUrl()}${RESEARCH_AGENT_THREADS_ENDPOINT}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    )

    if (!response.ok) {
      const body = await response.text().catch(() => "")
      throw new Error(
        `Failed to create thread (${response.status}): ${
          body || response.statusText
        }`
      )
    }

    return (await response.json()) as ThreadResponse
  }

  /**
   * Streams AI-generated research text.
   * onChunk is invoked for each decoded text chunk.
   */
  public async streamResearch(
    q: string,
    threadId: string,
    token: string,
    onChunk: (text: string) => void
  ): Promise<void> {
    const url = new URL(`${this.getBaseUrl()}${RESEARCH_AGENT_STREAM_ENDPOINT}`)
    url.searchParams.set("q", q)
    url.searchParams.set("thread_id", threadId)

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })

    if (!response.ok || !response.body) {
      const body = await response.text().catch(() => "")
      throw new Error(
        `Failed to stream research (${response.status}): ${
          body || response.statusText
        }`
      )
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    try {
      let isDone = false
      while (!isDone) {
        const { value, done } = await reader.read()
        isDone = done
        if (isDone) break
        const chunk = decoder.decode(value, { stream: true })
        if (chunk) onChunk(chunk)
      }
    } finally {
      reader.releaseLock()
    }
  }
}

const ResearchAgentAPIInstance = new ResearchAgentAPI()
export default ResearchAgentAPIInstance
